import API from '../apiClient';
import { countryToCurrency, pickPriceForCurrency } from '../Utils/frameworkTiers';

/**
 * ========================================
 * BILLING API
 * ========================================
 * APIs for Stripe product listing, subscription status, checkout, and payment links.
 * Product lines: "Zavior Platform" and "Zavior Consult"
 */

/**
 * GetBillingProducts - Get all active Zavior products (Platform + Consult) with prices
 * @returns {Promise}
 */
export const GetBillingProducts = () => {
  return new Promise((resolve, reject) => {
    API.get('/billing/products')
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('GetBillingProducts error:', error);
      });
  });
};

/**
 * GetPlatformProducts - Get Zavior Platform products only
 * @returns {Promise}
 */
export const GetPlatformProducts = () => {
  return new Promise((resolve, reject) => {
    API.get('/billing/products', { params: { product_line: 'Zavior Platform' } })
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('GetPlatformProducts error:', error);
      });
  });
};

/**
 * GetConsultProducts - Get Zavior Consult products only
 * @returns {Promise}
 */
export const GetConsultProducts = () => {
  return new Promise((resolve, reject) => {
    API.get('/billing/products', { params: { product_line: 'Zavior Consult' } })
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('GetConsultProducts error:', error);
      });
  });
};

/**
 * GetBillingOverview - Zavior org + Stripe customer + payload + subscriptions (Billing Admin).
 * @param {string} orgId - Organization ID
 * @returns {Promise}
 */
export const GetBillingOverview = (orgId) => {
  return new Promise((resolve, reject) => {
    API.get(`/billing/overview/${encodeURIComponent(orgId)}`)
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('GetBillingOverview error:', error);
      });
  });
};

/**
 * GetSubscriptionStatus - Get Stripe subscription status for an organization.
 * Also syncs is_paid_client and current_subscription_start_date back to the DB.
 * @param {string} orgId - Organization ID
 * @returns {Promise}
 */
export const GetSubscriptionStatus = (orgId) => {
  return new Promise((resolve, reject) => {
    API.get(`/billing/subscription/${encodeURIComponent(orgId)}`)
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('GetSubscriptionStatus error:', error);
      });
  });
};

/**
 * CreateCheckoutSession - Create a Stripe Checkout Session and return the redirect URL.
 * @param {string} orgId - Organization ID
 * @param {string} priceId - Stripe Price ID
 * @param {number} quantity - Number of units/seats
 * @param {string} successUrl - URL to redirect to after successful payment
 * @param {string} cancelUrl - URL to redirect to if user cancels
 * @returns {Promise} - Resolves with { url: string }
 */
export const CreateCheckoutSession = (orgId, priceId, quantity, successUrl, cancelUrl, options = {}) => {
  return new Promise((resolve, reject) => {
    const payload = {
      org_id: orgId,
      price_id: priceId,
      quantity,
      success_url: successUrl,
      cancel_url: cancelUrl,
    };
    if (options.anchorDay === 1 || options.anchorDay === 15) {
      payload.anchor_day = options.anchorDay;
    }
    if (options.couponId) {
      payload.coupon_id = options.couponId;
    }
    API.post('/billing/checkout', payload)
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('CreateCheckoutSession error:', error);
      });
  });
};

/**
 * GetPaymentLinks - Get all active Stripe Payment Links
 * @returns {Promise}
 */
export const GetPaymentLinks = () => {
  return new Promise((resolve, reject) => {
    API.get('/billing/payment-links')
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('GetPaymentLinks error:', error);
      });
  });
};

/**
 * ConnectStripeCustomer - Link an org to a Stripe customer.
 * If stripeCustomerId is empty, the backend auto-searches or creates.
 * @param {string} orgId
 * @param {string} [stripeCustomerId]
 * @returns {Promise}
 */
export const ConnectStripeCustomer = (orgId, stripeCustomerId = '') => {
  return new Promise((resolve, reject) => {
    API.post('/billing/customer/connect', { org_id: orgId, stripe_customer_id: stripeCustomerId })
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('ConnectStripeCustomer error:', error);
      });
  });
};

/**
 * RepointStripeCustomer - Bulk-update all orgs from one Stripe customer to another.
 * @param {string} fromCustomerId
 * @param {string} toCustomerId
 * @returns {Promise}
 */
export const RepointStripeCustomer = (fromCustomerId, toCustomerId) => {
  return new Promise((resolve, reject) => {
    API.post('/billing/customer/repoint', { from_customer_id: fromCustomerId, to_customer_id: toCustomerId })
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('RepointStripeCustomer error:', error);
      });
  });
};

/**
 * CancelSubscription - Cancel the active subscription for an org.
 * @param {string} orgId
 * @param {boolean} [cancelAtPeriodEnd=true]
 * @returns {Promise}
 */
export const CancelSubscription = (orgId, cancelAtPeriodEnd = true) => {
  return new Promise((resolve, reject) => {
    API.post('/billing/subscription/cancel', { org_id: orgId, cancel_at_period_end: cancelAtPeriodEnd })
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('CancelSubscription error:', error);
      });
  });
};

/**
 * UpdateSubscription - Change the price plan on an active subscription.
 * @param {string} orgId
 * @param {string} priceId - New Stripe Price ID
 * @returns {Promise}
 */
export const UpdateSubscription = (orgId, priceId, subscriptionId = null) => {
  return new Promise((resolve, reject) => {
    const payload = { org_id: orgId, price_id: priceId };
    if (subscriptionId) payload.subscription_id = subscriptionId;
    API.put('/billing/subscription/update', payload)
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('UpdateSubscription error:', error);
      });
  });
};

/**
 * UpdateSubscriptionQuantity - Change the unit quantity on an active subscription.
 * @param {string} orgId
 * @param {number} quantity
 * @returns {Promise}
 */
export const UpdateSubscriptionQuantity = (orgId, quantity) => {
  return new Promise((resolve, reject) => {
    API.put('/billing/subscription/quantity', { org_id: orgId, quantity })
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('UpdateSubscriptionQuantity error:', error);
      });
  });
};

/**
 * CreatePortalSession - Open the Stripe Billing Portal for self-service.
 * @param {string} orgId
 * @param {string} returnUrl - URL to redirect back to after the portal
 * @returns {Promise} - Resolves with { url: string }
 */
export const CreatePortalSession = (orgId, returnUrl) => {
  return new Promise((resolve, reject) => {
    API.post('/billing/portal', { org_id: orgId, return_url: returnUrl })
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('CreatePortalSession error:', error);
      });
  });
};

/**
 * GetInvoices - Get invoice history for an org.
 * @param {string} orgId
 * @returns {Promise}
 */
export const GetInvoices = (orgId) => {
  return new Promise((resolve, reject) => {
    API.get(`/billing/invoices/${encodeURIComponent(orgId)}`)
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('GetInvoices error:', error);
      });
  });
};

/**
 * GetUpcomingInvoice - Get the next scheduled invoice for an org.
 * @param {string} orgId
 * @returns {Promise}
 */
export const GetUpcomingInvoice = (orgId) => {
  return new Promise((resolve, reject) => {
    API.get(`/billing/invoices/${encodeURIComponent(orgId)}/upcoming`)
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('GetUpcomingInvoice error:', error);
      });
  });
};

/**
 * ApplyCoupon - Apply a discount coupon to an org's active subscription.
 * @param {string} orgId
 * @param {string} couponId - Stripe Coupon ID
 * @returns {Promise}
 */
export const ApplyCoupon = (orgId, couponId) => {
  return new Promise((resolve, reject) => {
    API.post('/billing/coupon/apply', { org_id: orgId, coupon_id: couponId })
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('ApplyCoupon error:', error);
      });
  });
};

/**
 * ========================================
 * FRAMEWORK TIER ENTITLEMENTS
 * ========================================
 * Per-organization framework tier gating. The caller org is derived from the
 * JWT server-side; these self-service calls never take an org_id.
 */

/** GetMyEntitlements - the caller org's purchased + effective tiers */
export const GetMyEntitlements = () => {
  return new Promise((resolve, reject) => {
    API.get('/entitlement/my')
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('GetMyEntitlements error:', error);
      });
  });
};

/** GetEntitlementCatalog - Stripe-derived sellable SKU catalog (Essentials + paid tiers) */
export const GetEntitlementCatalog = () => {
  return new Promise((resolve, reject) => {
    API.get('/entitlement/catalog')
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('GetEntitlementCatalog error:', error);
      });
  });
};

/**
 * startPlatformCheckout - platform-level "Make Payment" CTA (Onboarding / Focus
 * / Trial-Expired pages). Sells ONLY the Essentials base plan ($200/mo) — the
 * bare-minimum platform subscription that reactivates an org (is_paid_client).
 * Resolves the Essentials price for the org's currency and redirects to Stripe
 * Checkout, so the purchase carries the org id and the webhook keeps the org
 * active past its trial. Rejects (caller shows a toast) if no plan/price/url
 * is available. Higher tiers are sold via UpgradeModal, never from this CTA.
 */
export const startPlatformCheckout = async () => {
  const orgId = localStorage.getItem('organizationId');
  const currency = countryToCurrency(localStorage.getItem('country'));
  const res = await GetEntitlementCatalog();
  const catalog = res?.data?.data || res?.data || [];
  // Essentials only — do NOT fall back to Advance/Premium here.
  const pick = catalog.find((c) => c.sku === 'Essentials');
  if (!pick) throw new Error('The Essentials plan is not available yet. Please contact your administrator.');
  const price = pickPriceForCurrency(pick.prices, currency);
  if (!price?.price_id) throw new Error('No Essentials price is configured for your region yet.');
  const successUrl = `${window.location.origin}/adminlist?checkout=success`;
  const cancelUrl = window.location.href;
  const checkout = await CreateCheckoutSession(orgId, price.price_id, 1, successUrl, cancelUrl);
  const url = checkout?.data?.url;
  if (!url) throw new Error('Could not start checkout');
  window.location.assign(url);
};

/** RedeemEntitlementCode - redeem an unlock code for the caller's org */
export const RedeemEntitlementCode = (code) => {
  return new Promise((resolve, reject) => {
    API.post('/entitlement/redeem', { code })
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('RedeemEntitlementCode error:', error);
      });
  });
};

/** ResyncEntitlements - reconcile the caller org's tiers from Stripe (post-checkout) */
export const ResyncEntitlements = () => {
  return new Promise((resolve, reject) => {
    API.post('/entitlement/resync')
      .then(response => resolve(response))
      .catch(error => {
        reject(error);
        console.error('ResyncEntitlements error:', error);
      });
  });
};

/**
 * ========================================
 * ENTITLEMENT ADMIN (Tech Admin — BillingAdmin "Entitlements" tab)
 * ========================================
 */

/** GetSeatStatus - staff/vendor/guest counts + billed seat band for an org */
export const GetSeatStatus = (orgId) => API.get(`/billing/seat-status/${orgId}`);

/** GetOrgEntitlements - list an org's tier entitlement rows */
export const GetOrgEntitlements = (orgId) =>
  API.get('/entitlement/', { params: orgId ? { organization_id: orgId } : {} });

/** GrantEntitlement - manually grant one or more tiers to an org.
 * payload: { tiers: string[], starts_at?, expires_at?, notes?, message? }
 * A future starts_at makes the grant "pending" (access opens on that date, no email).
 */
export const GrantEntitlement = (organizationId, payload = {}) =>
  API.post('/entitlement/', { organization_id: organizationId, ...payload });

/** RevokeEntitlement - soft-delete (revoke) an entitlement row */
export const RevokeEntitlement = (id) => API.delete(`/entitlement/${id}`);

/** AdminResyncEntitlements - reconcile any org's tiers from Stripe (billing admin) */
export const AdminResyncEntitlements = (orgId) => API.post(`/entitlement/resync/${orgId}`);

/** ListEntitlementCodes - all unlock codes */
export const ListEntitlementCodes = () => API.get('/entitlement-code/');

/** ListCodeRedemptions - redemption history for one code (org name + redeemer email) */
export const ListCodeRedemptions = (codeId) => API.get(`/entitlement-code/${codeId}/redemptions`);

/** CreateEntitlementCode - create an unlock code.
 * payload: { code, tiers: string[], max_uses?, valid_until?, grant_duration_days?, is_active, notes? }
 */
export const CreateEntitlementCode = (payload) => API.post('/entitlement-code/', payload);

/** UpdateEntitlementCode - update an unlock code */
export const UpdateEntitlementCode = (id, payload) => API.put(`/entitlement-code/${id}`, payload);

/** DeleteEntitlementCode - soft-delete an unlock code */
export const DeleteEntitlementCode = (id) => API.delete(`/entitlement-code/${id}`);

/** ClearPendingDiscount - clear org's armed partner checkout coupon */
export const ClearPendingDiscount = (orgId) =>
  API.post('/billing/clear-pending-discount', orgId ? { org_id: orgId } : {});

/** GetStripeCoupon - Tech Admin sync of Stripe coupon + promotion codes */
export const GetStripeCoupon = (couponId) =>
  API.get(`/billing/stripe-coupon/${encodeURIComponent(couponId)}`);
